<style scoped>
.date-row {
	display: flex;
	gap: 1rem;
	align-items: flex-end;
	margin-bottom: 1rem;
	flex-wrap: wrap;
}

.date-field {
	flex: 0 0 auto;
}

.date-field label {
	display: block;
	margin-bottom: 0.375rem;
	font-size: 0.875rem;
	font-weight: 600;
	color: #4a5568;
}

.date-field input[type="date"] {
	padding: 0.5rem 0.75rem;
	border: 1px solid #cbd5e0;
	border-radius: 6px;
	font-size: 0.9rem;
	background: white;
	min-width: 180px;
}

.date-field input[type="date"]:focus {
	outline: none;
	border-color: #4299e1;
	box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
}
</style>

<template>
	<div>
		<h5 class="mb-3">
			<i class="bi bi-cloud-upload me-2"></i>Upload Custom Pool Data
		</h5>

		<p class="text-muted mb-4">
			Upload a CSV file containing custom pool operations data.
		</p>

		<div class="date-row">
			<div class="date-field">
				<label for="pool-date">
					<i class="bi bi-calendar3 me-1"></i>Data Date
				</label>
				<input
					id="pool-date"
					type="date"
					v-model="selectedDate"
					class="form-control"
				/>
			</div>
		</div>

		<CSVUpload
			:uploadUrl="'/api/upload/pool/custom'"
			:additionalData="{ date: selectedDate }"
			@doSuccess="handleSuccess"
			@doError="handleError"
		/>

		<div class="mt-4">
			<TableUploadRequirements :requirements="requirements" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import CSVUpload from "@/components/CSVUpload.vue";
import TableUploadRequirements, {
	UploadRequirement,
} from "@/components/upload/TableUploadRequirements.vue";

const router = useRouter();

const requirements = ref<UploadRequirement[]>([
	{
		field: "Street Address",
		description: "Street number and name of the address of the pool",
		format: "Text",
		example: "123 Main St.",
		is_required: true,
	},
	{
		field: "City",
		description: "The city portion of the pool's address",
		format: "Text",
		example: "Visalia",
		is_required: true,
	},
	{
		field: "State",
		description: "The state portion of the pool's address",
		format: "Text",
		example: "'California' or 'CA'",
		is_required: true,
	},
	{
		field: "Notes",
		description:
			" Any notes from the district to include with the pool record ",
		format: "Text",
		example: '"Collects rain water when empty"',
		is_required: false,
	},
	{
		field: "Postal Code",
		description: "Postal (Zip) Code of the pool's address",
		format: "numbers and optional hypen",
		example: "81234 or 91234-5678",
		is_required: true,
	},
	{
		field: "Pool Condition",
		description: "The condition of the pool when it was last inspected",
		format: "Text",
		example: '"blue", "dry", "false pool", "green", or "murky"',
		is_required: false,
	},
	{
		field: "Property Owner Name",
		description: "Name of the person or entity that owns the property",
		format: "Text",
		example: "No",
		is_required: false,
	},
	{
		field: "Property Owner Phone",
		description: "Phone number of the person or entity that owns the property",
		format: "e164",
		example:
			'"+14155552671" or "1-(901)-555-1234" or "9015551234" or "1901-555-12-34"',
		is_required: false,
	},
	{
		field: "Resident Owned",
		description:
			" Whether or not the current resident of the property is also the owner ",
		format: "Yes, No, or empty",
		example: '"Yes" or "No" or ""',
		is_required: false,
	},
	{
		field: "Resident Phone",
		description: "Phone number of the resident",
		format: "e164",
		example:
			'"+14155552671" or "1-(901)-555-1234" or "9015551234" or "1901-555-12-34"',
		is_required: false,
	},
	{
		field: "Tags",
		description:
			" Any additional columns in the file will be treated as tags and attached to the record ",
		format: "Text",
		example: '"Hostile" or "Unresponsive" or "Dog"',
		is_required: false,
	},
]);
const today = new Date();
const selectedDate = ref(
	`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
);

function handleSuccess(path: string) {
	router.push(path);
}

function handleError(error: Error) {
	console.error("Upload failed:", error);
}
</script>
